import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  Skeleton,
  Tooltip
} from '@mui/material'
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  DeleteForever,
  Refresh,
  ScheduleOutlined,
  CalendarTodayOutlined,
  QrCode2Outlined,
  LocalShippingOutlined,
  FlightTakeoffOutlined,
  FlightLandOutlined,
  TrendingUpOutlined
} from '@mui/icons-material'

import { useGlobalStoreData } from '../../store/globalStore'

import { countryEN } from '../../assets/data/country-en'
import { countryTR } from '../../assets/data/country-tr'

import {
  HsCell,
  CountryCell,
  EngagementCell,
  formatDate
} from './CampaignTableCells'

const getCountryName = (countryCode, language = 'en') => {
  const countryData = language === 'tr' ? countryTR : countryEN
  const country = countryData.find((c) => c.countryCode === countryCode)
  return country ? country.countryName : countryCode
}

export const GRID_TEMPLATE = '1.2fr 1fr 1fr 0.9fr 0.8fr 0.8fr 2.3fr 0.6fr'
const EMPTY_DATE_LABEL = '—'

export const CampaignTableRow = ({
  campaignItem,
  campaignReport,
  campaignReportFetching,
  setConfirmationModalItem,
  refreshCampaignReport
}) => {
  const { t, i18n } = useTranslation()
  const languageGSD = useGlobalStoreData.language()
  const hsCodesGSD = useGlobalStoreData.hsCodes()

  const linkTarget = `${process.env.PUBLIC_URL}/${languageGSD}/campaigns/detail?b2bAccountId=${campaignItem.b2bAccountId}&b2bCampaignId=${campaignItem.id}`

  const productNames = campaignItem.productName || []
  const maxProducts = 2
  const lastChecked = formatDate(campaignReport?.inboxLastChecked)
  const createdAt = formatDate(campaignItem.createdAt)

  const fromCode = campaignItem.currentCountryCode
  const toCode = campaignItem.importerCountryCodes?.[0]
  const fromName = getCountryName(fromCode, languageGSD)
  const toName = getCountryName(toCode, languageGSD)

  const totalMail =
    (campaignReport?.outgoingEmailStats?.DELIVERED || 0) +
    (campaignReport?.outgoingEmailStats?.FAILED || 0)
  const replyPercent =
    totalMail && campaignReport?.repliedEmailCount
      ? ((campaignReport.repliedEmailCount * 100) / totalMail).toFixed(1)
      : null

  return (
    <Box
      component={Link}
      to={linkTarget}
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE,
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 2,
        textDecoration: 'none',
        color: 'inherit',
        position: 'relative',
        borderBottom: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.04)',
        transition: 'all 0.2s ease',
        '&:nth-of-type(even)': {
          backgroundColor: 'rgba(0, 0, 0, 0.012)'
        },
        '&:last-of-type': { borderBottom: 'none' },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: '0 4px 4px 0',
          background: 'linear-gradient(180deg, #14b8a6 0%, #0d9488 100%)',
          opacity: 0,
          transform: 'translateX(-3px)',
          transition: 'all 0.2s ease'
        },
        '&:hover': {
          background: 'linear-gradient(90deg, rgba(0, 150, 136, 0.05) 0%, rgba(0, 150, 136, 0.01) 100%)',
          '&::before': { opacity: 1, transform: 'translateX(0)' },
          '& .row-actions': { opacity: 1 },
          '& .row-title': { color: 'primary.main' }
        }
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          className="row-title"
          sx={{
            fontWeight: 500,
            fontSize: '0.9rem',
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            transition: 'color 0.18s ease',
            letterSpacing: -0.1,
            mb: 0.5
          }}
        >
          {campaignItem.name}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {productNames.slice(0, maxProducts).map((product, idx) => (
            <Chip
              key={idx}
              label={product}
              size="small"
              sx={{
                borderRadius: 1.25,
                height: 20,
                fontSize: '0.7rem',
                background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                color: 'text.secondary',
                border: '1px solid rgba(0, 0, 0, 0.04)',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          ))}
          {productNames.length > maxProducts && (
            <Tooltip title={productNames.slice(maxProducts).join(', ')} arrow>
              <Chip
                label={`+${productNames.length - maxProducts}`}
                size="small"
                sx={{
                  borderRadius: 1.25,
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: 'grey.200',
                  fontWeight: 500,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      <CountryCell code={fromCode} name={fromName} />

      <CountryCell code={toCode} name={toName} />

      <HsCell
        hsArr={campaignItem?.hsCodes}
        hsCodesGSD={hsCodesGSD}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
        {createdAt
          ? <>
              <ScheduleOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.secondary' }}>{createdAt}</Typography>
            </>
          : <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.disabled' }}>{EMPTY_DATE_LABEL}</Typography>
        }
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
        {lastChecked
          ? <>
              <ScheduleOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.secondary' }}>{lastChecked}</Typography>
            </>
          : <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'text.disabled' }}>{EMPTY_DATE_LABEL}</Typography>
        }
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {campaignReportFetching || !campaignReport
          ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[0, 1, 2, 3].map((i) => (
                  <Box key={i} sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
                    <Skeleton variant="circular" width={26} height={26} />
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Skeleton variant="rectangular" width="60%" height={12} sx={{ borderRadius: 0.5 }} />
                      <Skeleton variant="rectangular" width="80%" height={8} sx={{ borderRadius: 0.5 }} />
                    </Box>
                  </Box>
                ))}
              </Box>
              <Skeleton variant="rectangular" width="calc(100% - 16px)" height={8} sx={{ borderRadius: 999, mx: 1 }} />
            </Box>
          : <EngagementCell
              campaignReport={campaignReport}
              repliedLabel={t('mailTemplate.campaigns.emailReport.repliedEmailCount')}
              replyPercent={replyPercent}
              locale={i18n.language}
              statusLabels={{
                DELIVERED: t('mailTemplate.campaigns.emailReport.DELIVERED'),
                WAITING: t('mailTemplate.campaigns.emailReport.WAITING'),
                FAILED: t('mailTemplate.campaigns.emailReport.FAILED')
              }}
              sentLabel={t('mailTemplate.campaigns.engagement.sent') || 'Sent'}
              deliveredShortLabel={t('mailTemplate.campaigns.engagement.delivered') || 'Delivered'}
              incomingShortLabel={t('mailTemplate.campaigns.engagement.incoming') || 'Incoming'}
              repliedShortLabel={t('mailTemplate.campaigns.engagement.replied') || 'Replied'}
            />
        }
      </Box>

      <Box
        className="row-actions"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 0.25,
          opacity: { xs: 1, md: 0 },
          transition: 'opacity 0.18s ease'
        }}
      >
        <Tooltip title={t('mailTemplate.campaigns.modal.refresh') || 'Refresh'} arrow>
          <IconButton
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              !campaignReportFetching && refreshCampaignReport(campaignItem.id)
            }}
            size="small"
            sx={{
              color: 'primary.main',
              '&:hover': { backgroundColor: 'rgba(0, 150, 136, 0.12)' }
            }}
          >
            {campaignReportFetching
              ? <CircularProgress size={14} />
              : <Refresh sx={{ fontSize: '1.05rem' }} />
            }
          </IconButton>
        </Tooltip>
        <Tooltip title={t('mailTemplate.campaigns.delete')} arrow>
          <IconButton
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setConfirmationModalItem(campaignItem)
            }}
            size="small"
            sx={{
              color: 'error.main',
              '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.1)' }
            }}
          >
            <DeleteForever sx={{ fontSize: '1.05rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

export const CampaignTableHeader = () => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE,
        gap: 2,
        px: 2.5,
        py: 1.5,
        background: 'linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%)',
        borderBottom: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        boxShadow: '0 1px 0 rgba(0, 0, 0, 0.02)'
      }}
    >
      {[
        { label: t('mailTemplate.campaigns.tableHeaders.campaign') || 'Campaign', icon: LocalShippingOutlined },
        { label: t('mailTemplate.campaigns.tableHeaders.exporter') || 'Exporter', icon: FlightTakeoffOutlined },
        { label: t('mailTemplate.campaigns.tableHeaders.importer') || 'Importer', icon: FlightLandOutlined },
        { label: t('mailTemplate.campaigns.tableHeaders.hsCode') || 'HS Code', icon: QrCode2Outlined },
        { label: t('mailTemplate.campaigns.tableHeaders.createdAt') || 'Created', icon: CalendarTodayOutlined },
        { label: t('mailTemplate.campaigns.tableHeaders.lastChecked') || 'Last Checked', icon: ScheduleOutlined },
        { label: t('mailTemplate.campaigns.tableHeaders.engagement') || 'Engagement', icon: TrendingUpOutlined },
        { label: '', icon: null }
      ].map(({ label, icon: Icon }, idx) => (
        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.625, minWidth: 0 }}>
          {Icon && <Icon sx={{ fontSize: 13, color: 'text.disabled' }} />}
          <Typography
            sx={{
              fontSize: '0.68rem',
              fontWeight: 500,
              color: 'text.disabled',
              letterSpacing: 0.8,
              textTransform: 'uppercase'
            }}
          >
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

export const CampaignTableEmptyState = ({ onCreateCampaign }) => {
  const { t } = useTranslation()

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2 }}>
      <Paper
        elevation={0}
        sx={{
          textAlign: 'center',
          p: 8,
          maxWidth: 600,
          width: '100%',
          backgroundColor: 'grey.50',
          border: '2px dashed',
          borderColor: 'grey.300',
          borderRadius: 3
        }}
      >
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <CampaignIcon sx={{ fontSize: 80, color: 'primary.light', opacity: 0.7 }} />
        </Box>
        <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
          {t('mailTemplate.campaigns.noCampaigns')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          {t('mailTemplate.campaigns.createFirstCampaign')}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onCreateCampaign}
          startIcon={<AddIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4, py: 1.5 }}
        >
          {t('mailTemplate.campaigns.createCampaign')}
        </Button>
      </Paper>
    </Box>
  )
}

export const CreateCampaignBanner = ({ onCreateCampaign }) => {
  const { t } = useTranslation()

  return (
    <Card
      onClick={onCreateCampaign}
      elevation={0}
      sx={{
        mb: 2,
        cursor: 'pointer',
        borderRadius: 2.5,
        border: '1.5px dashed',
        borderColor: 'primary.light',
        backgroundColor: 'rgba(0, 150, 136, 0.025)',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'rgba(0, 150, 136, 0.06)',
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 16px rgba(0, 150, 136, 0.12)'
        }
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 150, 136, 0.12)',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <AddIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'primary.main', lineHeight: 1.2 }}>
            {t('mailTemplate.campaigns.createCampaign')}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
            {t('mailTemplate.campaigns.createFirstCampaign')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
